import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, Select, Button, message, Card, Empty, Space, Switch } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { getAdminUserById, updateUserInfo } from '../api';

const { Option } = Select;

const UserRole = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  const loadUserInfo = async () => {
    try {
      setLoading(true);
      const response = await getAdminUserById(id);
      const targetUser = response?.success ? response.data : null;

      if (!targetUser || targetUser.id == null) {
        message.error('用户不存在');
        navigate('/user/list');
        return;
      }

      setUser(targetUser);
      form.setFieldsValue({
        id: targetUser.id,
        real_name_note: (targetUser.real_name && String(targetUser.real_name).trim()) || '无',
        nickname: targetUser.nickname || '',
        phone: targetUser.phone || '',
        service_phone: targetUser.service_phone || '',
        openid: targetUser.openid || '未绑定',
        mp_openid: targetUser.mp_openid || '未绑定',
        unionid: targetUser.unionid || '未绑定',
        user_type: [1, 2].includes(Number(targetUser.user_type))
          ? Number(targetUser.user_type)
          : 1,
        zklanyash: Number(targetUser.zklanyash) === 1,
        show_feedback: Number(targetUser.show_feedback) === 1,
      });
    } catch (error) {
      console.error('加载用户信息失败：', error);
      message.error('加载用户信息失败，请检查接口');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadUserInfo();
    } else {
      message.error('缺少用户 ID');
      navigate('/user/list');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const submitData = {
        new_id: values.id ? Number(values.id) : undefined,
        nickname: values.nickname || '',
        phone: values.phone ? values.phone.trim() : '',
        service_phone: values.service_phone != null ? String(values.service_phone).trim() : '',
        user_type: Number(values.user_type),
        zklanyash: values.zklanyash ? 1 : 0,
        show_feedback: values.show_feedback ? 1 : 0,
      };

      const res = await updateUserInfo(id, submitData);
      if (res?.success) {
        message.success('用户信息修改成功');
        navigate('/user/list');
      } else {
        message.error(res?.msg || '修改用户信息失败');
      }
    } catch (error) {
      console.error('修改用户信息失败：', error);
      if (error?.errorFields) {
        message.error('请检查表单填写是否正确');
      } else {
        message.error('修改用户信息失败，请稍后重试');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '50px' }}>加载中...</div>;
  }

  if (!user) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Empty description="用户不存在" />
      </div>
    );
  }

  return (
    <Card
      title={
        <Space>
          修改用户信息
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/user/list')}
            style={{ fontSize: '14px', color: '#1890ff' }}
          >
            返回列表
          </Button>
        </Space>
      }
      style={{ maxWidth: 500, margin: '20px auto', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.09)' }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ user_type: user.user_type }}
        autoComplete="off"
      >
        <Form.Item
          name="id"
          label="用户ID（测试用，可修改）"
          rules={[{ required: true, message: '用户ID不能为空' }]}
        >
          <Input placeholder="请输入新的用户ID（仅测试环境使用）" style={{ borderRadius: 4 }} />
        </Form.Item>

        <Form.Item name="real_name_note" label="真实姓名（只读，来自扩展表/银行卡名等）">
          <Input disabled style={{ color: 'rgba(0,0,0,0.65)' }} />
        </Form.Item>

        <Form.Item
          name="nickname"
          label="用户昵称"
          rules={[{ max: 20, message: '昵称长度不能超过20个字' }]}
        >
          <Input placeholder="请输入用户昵称（选填）" style={{ borderRadius: 4 }} />
        </Form.Item>

        <Form.Item
          name="phone"
          label="手机号"
          rules={[
            { required: true, message: '手机号不能为空' },
            { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的11位手机号（如：13800138000）' },
            { max: 11, message: '手机号长度不能超过11位' },
          ]}
          getValueFromEvent={(e) => e.target.value.trim()}
        >
          <Input placeholder="请输入手机号（必填）" style={{ borderRadius: 4 }} maxLength={11} />
        </Form.Item>

        <Form.Item
          name="service_phone"
          label="售后手机号"
          extra="选填；租客充值页售后电话优先展示此号码，为空时回退登录手机号"
          rules={[{ max: 20, message: '售后手机号不能超过20个字符' }]}
          getValueFromEvent={(e) => e.target.value.trim()}
        >
          <Input placeholder="请输入售后手机号（选填）" style={{ borderRadius: 4 }} maxLength={20} />
        </Form.Item>

        <Form.Item name="openid" label="OpenID" rules={[{ required: false }]}>
          <Input disabled placeholder="未绑定" style={{ borderRadius: 4, backgroundColor: '#f5f5f5' }} />
        </Form.Item>

        <Form.Item name="mp_openid" label="公众号OpenID" rules={[{ required: false }]}>
          <Input disabled placeholder="未绑定" style={{ borderRadius: 4, backgroundColor: '#f5f5f5' }} />
        </Form.Item>

        <Form.Item name="unionid" label="UnionID" rules={[{ required: false }]}>
          <Input disabled placeholder="未绑定" style={{ borderRadius: 4, backgroundColor: '#f5f5f5' }} />
        </Form.Item>

        <Form.Item
          name="user_type"
          label="用户角色"
          rules={[
            { required: true, message: '请选择用户角色' },
            { type: 'number', message: '角色类型必须是数字' },
          ]}
        >
          <Select
            placeholder="请选择角色"
            style={{ borderRadius: 4 }}
            onChange={(value) => form.setFieldsValue({ user_type: Number(value) })}
          >
            <Option value={1}>房东</Option>
            <Option value={2}>租客</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="zklanyash"
          label="开启蓝牙售后"
          valuePropName="checked"
          extra="仅对租客有效：在小程序「我的」中显示「蓝牙售后」入口，可输入表号抄表/充值 1～3 度"
        >
          <Switch checkedChildren="开" unCheckedChildren="关" />
        </Form.Item>

        <Form.Item
          name="show_feedback"
          label="显示意见反馈"
          valuePropName="checked"
          extra="在小程序「我的」中显示「意见反馈」入口，关闭后该用户不可见也不可提交"
        >
          <Switch checkedChildren="开" unCheckedChildren="关" />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            block
            style={{ borderRadius: 4, height: 40, fontSize: 16 }}
          >
            提交修改
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default UserRole;
